import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
} from "material-react-table";
import { useState } from "react";
import { useEffect } from "react";
import { useMemo, useRef } from "react";
import { Fragment } from "react";
import { connect } from "react-redux";

import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/FilterAltOff";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import FileDownloadIcon from "@mui/icons-material/NoteAdd";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";

import constants from "../../assets/constants";
import { createNewReport } from "../../data/omniDataModel";
import * as AdminSessionActions from "../../state/redux/adminSession";
import { createOmniPaginationObject, helperGetIsoDate } from "../../utils/helpers";
import { formatIsoDateString, getReportOnlyFields } from "../../utils/helpers";
import TextInputDialog from "../dialogs/textInputDialog";
import OmniGridButton from "../ui/omniGridButton";

const calculateColumns = userGroup => {
  //default case
  return [
    {
      accessorKey: "_id",
      header: "ID",
      size: 150,
      enableColumnFilterModes: false,
    },
    {
      accessorKey: "omniAudit.createdDate",
      enableColumnFilterModes: false,
      header: "Created",
      size: 125,

      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
      enableColumnFilter: false,
      //custom conditional format and styling
      Cell: ({ cell }) => <Box component="span">{`${formatIsoDateString(cell.getValue())}`}</Box>,
    },
    {
      accessorKey: "omniData.businessPillar",
      enableColumnFilterModes: false,
      header: "Pillar",
      minSize: 125,
      enableSorting: false,
      grow: false,

      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },

      filterFn: "equals",
      filterSelectOptions: [
        { text: "Unknown", value: "unknown" },
        { text: "Leadership", value: "leadership" },
        { text: "Operations", value: "operations" },
        { text: "Marketing", value: "marketing" },
        { text: "Finance", value: "finance" },
        { text: "Personnel", value: "personnel" },
      ],
      filterVariant: "select",

      //custom conditional format and styling
      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor:
              cell.getValue() === "leadership"
                ? theme.palette.highlight.main
                : cell.getValue() === "operations"
                  ? theme.palette.error.main
                  : cell.getValue() === "marketing"
                    ? theme.palette.warning.main
                    : cell.getValue() === "finance"
                      ? theme.palette.success.main
                      : cell.getValue() === "personnel"
                        ? theme.palette.info.main
                        : theme.palette.primary.main,
            borderRadius: "0.25rem",
            color: "#fff",
            maxWidth: "14ch",
            minWidth: "14ch",
            p: "0.25rem",
          })}
        >
          {constants.empirePillarTypes.filter(item => item.value === cell.getValue())[0].label}
        </Box>
      ),
    },
    {
      accessorKey: "omniData.familyName",
      header: "Last",
      size: 100,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
    },
    {
      accessorKey: "omniData.givenName",
      header: "First",
      size: 100,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
    },
    {
      accessorKey: "omniData.mobilePhone",
      header: "Phone",
      size: 100,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
    },
    {
      accessorKey: "omniData.email",
      header: "Email",
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
    },
  ];
};

const getInitialState = userGroup => {
  return {
    density: "compact",
    columnVisibility: {
      _id: false,
      "omniData.givenName": true,
      "omniData.familyName": true,
      "omniData.mobilePhone": false,
      "omniData.email": false,
      "omniAudit.createdDate": false,
    },
  };
};

export const SurveyProviders = props => {
  const {
    providerODMs,
    onEditProvider = null,
    onRequestNewPage = null,
    isLoading = false,
    providerPagination = createOmniPaginationObject,
    awsGroup,
  } = props;

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null); //we can get access to the underlying TableContainer element and react to its scroll events

  const columns = useMemo(() => calculateColumns(), []);
  const initialState = useMemo(() => getInitialState(), []);

  const [rowCount, setRowCount] = useState(0);

  const [showReportNameInput, setShowReportNameInput] = useState(null);

  useEffect(() => {
    if (providerPagination != null) {
      setPagination({
        pageIndex: providerPagination.pageIndex - 1,
        pageSize: providerPagination.pageSize,
      });
      setRowCount(providerPagination.totalItems);
    }
  }, [providerPagination]);

  const [sorting, setSorting] = useState([]);

  const [columnFilters, setColumnFilters] = useState([]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  useEffect(() => {
    let newQuery = {};
    columnFilters.forEach((element, index) => {
      if (element.id === "omniAudit.createdDate") {
        const min = parseInt(element.value[0]);
        const max = parseInt(element.value[1]);
        if (!isNaN(min)) {
          let theDateSring = helperGetIsoDate(min);

          newQuery[element.id] = {
            "#lte": theDateSring,
          };
        }
        if (!isNaN(max)) {
          let theDateSring = helperGetIsoDate(max);

          newQuery[element.id] = {
            "#lte": theDateSring,
          };
        }
      } else {
        newQuery[element.id] = {
          "#regex": `${element.value}`,
          "#options": "i",
        };
      }
    });

    let newSorting = {};
    sorting.forEach(element => {
      newSorting[element.id] = element.desc ? -1 : 1;
    });

    if (
      providerPagination &&
      (pagination.pageIndex + 1 !== providerPagination.pageIndex ||
        pagination.pageSize !== providerPagination.pageSize ||
        JSON.stringify(pagination.query) !== JSON.stringify(columnFilters))
    ) {
      let newPage = createOmniPaginationObject(providerPagination);
      newPage.pageIndex = pagination.pageIndex + 1;
      newPage.pageSize = pagination.pageSize;
      newPage.query = newQuery;
      newPage.sort = newSorting;

      onRequestNewPage(newPage);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, sorting, pagination.pageIndex, pagination.pageSize]);

  if (providerODMs == null) {
    return <Fragment></Fragment>;
  }

  return (
    <MaterialReactTable
      layoutMode="grid"
      columns={columns}
      data={providerODMs}
      initialState={initialState}
      defaultColumn={{
        minSize: 20, //allow columns to get smaller than default
        maxSize: 9001, //allow columns to get larger than default
        size: 100, //make columns wider by default
        grow: false,
      }}
      state={{
        columnFilters,
        isLoading,
        pagination,
        sorting,
      }} //pass our managed row selection state to the table to use
      //pagination
      manualPagination={true}
      onColumnFiltersChange={setColumnFilters}
      onPaginationChange={setPagination}
      manualSorting
      manualFiltering
      onSortingChange={setSorting}
      //header
      enableColumnActions={false}
      enableStickyHeader={true}
      enableColumnFilterModes={true}
      filterFns={{
        customFilterFn: (row, id, filterValue) => {
          return row.getValue(id) === filterValue;
        },
      }}
      muiTableHeadCellFilterTextFieldProps={{
        sx: {
          "& .MuiInputBase-input": { padding: "6px" },

          "& .MuiInputBase-root": { paddingRight: "0px" },
          "& .MuiSelect-iconOutlined": { marginRight: "0.75rem" },
          width: "100%",
        },
        variant: "outlined",
      }}
      //row
      // enableRowActions={true}
      enableRowActions={awsGroup === constants.userGroups.Administrator}
      enableRowSelection={false}
      getRowId={originalRow => originalRow._id}
      rowCount={rowCount}
      positionToolbarAlertBanner="bottom"
      muiTablePaginationProps={{
        rowsPerPageOptions: [5, 10, 20],
      }}
      muiTopToolbarProps={{
        sx: {
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
        },
      }}
      muiBottomToolbarProps={{
        sx: {
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.commonground.contrastText,
        },
      }}
      muiTablePaperProps={{
        sx: {
          boxShadow: "none",
        },
      }}
      muiTableContainerProps={{
        ref: tableContainerRef, //get access to the table container element
        sx: mobileLayout
          ? {
              minHeight: "564px",
              maxHeight: "564px",
              backgroundColor: theme.palette.commonground.main,
              color: theme.palette.commonground.main,
              borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.25)}`,
              borderRight: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.25)}`,
            }
          : {
              minHeight: "298px",
              maxHeight: "298px",
              backgroundColor: theme.palette.commonground.main,
              color: theme.palette.commonground.main,
              border: `solid 0.5px ${theme.palette.divider}`,
            },
      }}
      muiTableProps={{
        sx: {
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.commonground.main,
        },
      }}
      muiTableHeadCellProps={{
        sx: {
          backgroundColor: alpha(theme.palette.commonground.contrastText, 0.25),
          color: theme.palette.commonground.contrastText,
          justifyContent: "start",
        },
      }}
      renderRowActions={({ row }) => (
        <Box
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            gap: "2px",
            padding: 0,
          }}
        >
          <OmniGridButton
            key="edit"
            sx={{ ...(onEditProvider == null && { display: "none" }) }}
            onClick={() => {
              onEditProvider(row.original);
            }}
            icon={<EditIcon fontSize="small" />}
          />
        </Box>
      )}
      renderToolbarInternalActions={({ table }) => (
        <Box>
          {sorting.length > 0 && (
            <Tooltip title="Clear All Sorting" arrow>
              <IconButton
                sx={{ color: theme.palette.secondary.main }}
                onClick={() => {
                  setSorting([]);
                }}
              >
                <ClearSortingIcon />
              </IconButton>
            </Tooltip>
          )}
          {columnFilters.length > 0 && (
            <Tooltip title="Clear All Filters" arrow>
              <IconButton
                sx={{ color: theme.palette.secondary.main }}
                onClick={() => {
                  setColumnFilters([]);
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* eslint-disable react/jsx-pascal-case*/}
          {/* <MRT_ToggleGlobalFilterButton table={table} /> */}
          <MRT_ToggleFiltersButton table={table} />
          <MRT_ShowHideColumnsButton table={table} />
          <MRT_ToggleFullScreenButton table={table} />
          {/* eslint-enablereact/jsx-pascal-case */}

          <Tooltip title="Export Via Report" arrow>
            <IconButton
              onClick={() => {
                setShowReportNameInput(`${new Date().toJSON().slice(0, 10)}-Providers-`);
              }}
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>

          {showReportNameInput && (
            <TextInputDialog
              dialogTitle="Report Name"
              dialogMessage={"Please name your report"}
              inputLabel={`${"Report Name"}`}
              defaultValue={showReportNameInput}
              open={showReportNameInput}
              onAccept={theText => {
                /// do it!
                //create the report ..

                let theReport = createNewReport();
                theReport.omniData.name = theText;
                theReport.omniData.reportType = constants.reportTypeEnums.users;

                theReport.omniData["userType"] = "provider";

                theReport.omniData["onlyFields"] = getReportOnlyFields(
                  constants.reportTypeEnums.users,
                );
                let theQuery = providerPagination.query;

                theReport.omniData["query"] = theQuery;
                delete theReport.omniData.dateBefore;
                delete theReport.omniData.dateAfter;

                props.createReportRequest(theReport);

                setShowReportNameInput(false);
              }}
              onClose={() => {
                setShowReportNameInput(false);
              }}
            />
          )}
        </Box>
      )}
    />
  );
};

const mapStateToProps = state => ({
  awsGroup: state.userSession.awsGroup,
});

const mapDispatchToProps = {
  createReportRequest: AdminSessionActions.createReportRequest,
};
export default connect(mapStateToProps, mapDispatchToProps)(SurveyProviders);
